<template>
  <div class="orders-page" dir="rtl">
    <Navbar :showBackButton="true" @back="goBack" />

    <main class="orders-content">
      <header class="page-heading">
        <div>
          <span class="eyebrow">پنل حسابداری</span>
          <h1>سفارش‌های مشتریان</h1>
          <p>برای مشاهدهٔ اقلام و مبلغ نهایی، سفارش را انتخاب کنید.</p>
        </div>
        <span class="demo-badge">اطلاعات نمایشی</span>
      </header>

      <div class="orders-toolbar">
        <label class="search-box">
          <span aria-hidden="true">⌕</span>
          <input v-model="searchQuery" type="search" placeholder="جست‌وجو بر اساس نام، تلفن یا آدرس" />
        </label>
        <span class="orders-count">{{ toPersianDigits(filteredOrders.length) }} سفارش</span>
      </div>

      <section class="orders-list" aria-label="فهرست سفارش‌ها">
        <button
          v-for="order in filteredOrders"
          :key="order.id"
          class="order-card"
          type="button"
          @click="openOrder(order)"
        >
          <div class="order-card-top">
            <span class="order-date"><span aria-hidden="true">▦</span>{{ order.date }}</span>
            <span class="order-number">سفارش {{ toPersianDigits(order.id) }}</span>
            <span class="open-order" aria-hidden="true">‹</span>
          </div>
          <div class="customer-name">{{ order.customer.firstName }} {{ order.customer.lastName }}</div>
          <div class="customer-info-grid">
            <span class="info-item"><span class="info-label">تلفن</span><b>{{ order.customer.phone }}</b></span>
            <span class="info-item address-item"><span class="info-label">آدرس</span><b>{{ order.customer.address }}</b></span>
          </div>
          <div class="order-card-bottom">
            <span>{{ toPersianDigits(order.items.length) }} قلم کالا</span>
            <strong>{{ formatMoney(order.totalAmount) }} ریال</strong>
          </div>
        </button>

        <div v-if="!filteredOrders.length" class="empty-state">
          <span aria-hidden="true">⌕</span>
          <strong>سفارشی پیدا نشد</strong>
          <p>عبارت جست‌وجو را تغییر دهید.</p>
        </div>
      </section>
    </main>

    <div v-if="selectedOrder" class="detail-backdrop" @click.self="closeOrder">
      <section class="order-detail" role="dialog" aria-modal="true" :aria-label="`جزئیات سفارش ${selectedOrder.id}`">
        <header class="detail-header">
          <div>
            <span class="eyebrow">جزئیات سفارش</span>
            <h2>سفارش {{ toPersianDigits(selectedOrder.id) }}</h2>
          </div>
          <button class="close-button" type="button" aria-label="بستن" @click="closeOrder">×</button>
        </header>

        <div class="detail-customer">
          <div class="detail-customer-title">
            <span class="customer-avatar">{{ selectedOrder.customer.firstName.slice(0, 1) }}</span>
            <div>
              <strong>{{ selectedOrder.customer.firstName }} {{ selectedOrder.customer.lastName }}</strong>
              <span>{{ selectedOrder.date }} <i>·</i> {{ selectedOrder.customer.phone }}</span>
            </div>
          </div>
          <p><span>آدرس تحویل</span>{{ selectedOrder.customer.address }}</p>
        </div>

        <div class="detail-table-wrap">
          <table class="detail-table">
            <thead>
              <tr>
                <th>کد کالا</th>
                <th>نام کالا</th>
                <th>مقدار سفارش</th>
                <th>فی (ریال)</th>
                <th>تخفیف (ریال)</th>
                <th>قیمت نهایی (ریال)</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="item in selectedOrder.items" :key="item.code">
                <td data-label="کد کالا">{{ toPersianDigits(item.code) }}</td>
                <td class="item-name" data-label="نام کالا">{{ item.name }}</td>
                <td data-label="مقدار سفارش">{{ toPersianDigits(item.quantity) }} {{ item.unit }}</td>
                <td data-label="فی (ریال)">{{ formatMoney(item.unitPrice) }}</td>
                <td data-label="تخفیف (ریال)" class="discount-value">{{ formatMoney(item.discount) }}</td>
                <td data-label="قیمت نهایی (ریال)" class="final-value">{{ formatMoney(item.quantity * item.unitPrice - item.discount) }}</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colspan="4">جمع سفارش</th>
                <th class="discount-value">{{ formatMoney(selectedOrder.totalDiscount) }} ریال</th>
                <th class="final-value">{{ formatMoney(selectedOrder.totalAmount) }} ریال</th>
              </tr>
            </tfoot>
          </table>
        </div>

        <div class="mobile-totals" aria-label="جمع مبالغ سفارش">
          <div><span>جمع تخفیف</span><strong class="discount-value">{{ formatMoney(selectedOrder.totalDiscount) }} ریال</strong></div>
          <div><span>مبلغ نهایی</span><strong class="final-value">{{ formatMoney(selectedOrder.totalAmount) }} ریال</strong></div>
        </div>
      </section>
    </div>
  </div>
</template>

<script>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import Navbar from '@/components/Navbar.vue'

const sampleOrders = [
  {
    id: 5841,
    date: '۱۴۰۵/۰۸/۱۲',
    customer: { firstName: 'پوریا', lastName: 'حسینی', phone: '۰۹۱۲۱۲۳۴۵۶۷', address: 'تهران، خیابان آزادی، پلاک ۲۴، واحد ۳' },
    items: [
      { code: 1374, name: 'مبل راحتی هفت‌نفره', quantity: 1, unit: 'دستگاه', unitPrice: 125000000, discount: 5000000 },
      { code: 1563, name: 'میز جلو مبلی چوبی', quantity: 2, unit: 'عدد', unitPrice: 8500000, discount: 1000000 },
      { code: 2021, name: 'صندلی تک‌نفره', quantity: 2, unit: 'عدد', unitPrice: 12000000, discount: 0 },
    ],
    totalDiscount: 6000000,
    totalAmount: 160000000,
  },
  {
    id: 5838,
    date: '۱۴۰۵/۰۸/۱۱',
    customer: { firstName: 'نگار', lastName: 'محمدی', phone: '۰۹۱۲۷۶۵۴۳۲۱', address: 'کرج، عظیمیه، میدان مهران، کوچه یاس' },
    items: [
      { code: 1810, name: 'تخت خواب دونفره', quantity: 1, unit: 'دستگاه', unitPrice: 48000000, discount: 2400000 },
      { code: 1812, name: 'پاتختی', quantity: 2, unit: 'عدد', unitPrice: 6500000, discount: 500000 },
    ],
    totalDiscount: 2900000,
    totalAmount: 58100000,
  },
  {
    id: 5831,
    date: '۱۴۰۵/۰۸/۰۹',
    customer: { firstName: 'امیر', lastName: 'رضایی', phone: '۰۹۳۵۱۲۳۴۵۶۷', address: 'تهران، پاسداران، خیابان گلستان پنجم، پلاک ۱۱' },
    items: [
      { code: 1450, name: 'مبل ال مدل آریا', quantity: 1, unit: 'دستگاه', unitPrice: 97000000, discount: 7000000 },
      { code: 1992, name: 'میز عسلی سه‌تکه', quantity: 1, unit: 'ست', unitPrice: 9500000, discount: 500000 },
    ],
    totalDiscount: 7500000,
    totalAmount: 99000000,
  },
  {
    id: 5826,
    date: '۱۴۰۵/۰۸/۰۷',
    customer: { firstName: 'سارا', lastName: 'کریمی', phone: '۰۹۱۹۸۷۶۵۴۳۲', address: 'قم، بلوار امین، کوچه نسترن، پلاک ۸' },
    items: [
      { code: 2105, name: 'میز ناهارخوری شش‌نفره', quantity: 1, unit: 'دستگاه', unitPrice: 36000000, discount: 1800000 },
      { code: 2106, name: 'صندلی ناهارخوری', quantity: 6, unit: 'عدد', unitPrice: 4200000, discount: 3000000 },
    ],
    totalDiscount: 4800000,
    totalAmount: 56400000,
  },
]

export default {
  name: 'AccountantCustomerOrdersTest',
  components: { Navbar },
  setup() {
    const router = useRouter()
    const searchQuery = ref('')
    const selectedOrder = ref(null)
    const filteredOrders = computed(() => {
      const query = searchQuery.value.trim().toLocaleLowerCase()
      if (!query) return sampleOrders
      return sampleOrders.filter(({ customer, id }) =>
        `${id} ${customer.firstName} ${customer.lastName} ${customer.phone} ${customer.address}`.toLocaleLowerCase().includes(query)
      )
    })
    const formatMoney = value => new Intl.NumberFormat('fa-IR').format(Number(value) || 0)
    const toPersianDigits = value => String(value ?? '').replace(/\d/g, digit => '۰۱۲۳۴۵۶۷۸۹'[digit])
    const openOrder = order => { selectedOrder.value = order }
    const closeOrder = () => { selectedOrder.value = null }
    const onKeydown = event => { if (event.key === 'Escape') closeOrder() }
    const goBack = () => router.push('/accountant/dashboard')

    onMounted(() => window.addEventListener('keydown', onKeydown))
    onBeforeUnmount(() => window.removeEventListener('keydown', onKeydown))

    return { searchQuery, filteredOrders, selectedOrder, formatMoney, toPersianDigits, openOrder, closeOrder, goBack }
  },
}
</script>

<style scoped>
.orders-page { min-height: 100vh; background: var(--bg, #f4f7fb); color: var(--text, #172033); }
.orders-content { width: min(1100px, calc(100% - 32px)); margin: 0 auto; padding: 26px 0 56px; }
.page-heading { display: flex; align-items: center; justify-content: space-between; gap: 18px; margin: 8px 0 22px; }
.eyebrow { color: #2563eb; font-size: .78rem; font-weight: 800; }
.page-heading h1,.detail-header h2 { margin: 6px 0 5px; font-size: clamp(1.35rem, 3vw, 1.85rem); }
.page-heading p { margin: 0; color: #748096; font-size: .9rem; }
.demo-badge { flex: none; padding: 8px 12px; border: 1px solid #bfdbfe; border-radius: 999px; background: #eff6ff; color: #1d4ed8; font-size: .78rem; font-weight: 700; }
.orders-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 14px; margin-bottom: 16px; }
.search-box { display: flex; align-items: center; gap: 10px; width: min(100%, 480px); min-height: 46px; padding: 0 14px; border: 1px solid #dbe3ef; border-radius: 12px; background: var(--card-bg, #fff); }
.search-box span { color: #72809a; font-size: 1.5rem; line-height: 1; }
.search-box input { width: 100%; border: 0; outline: 0; background: transparent; color: inherit; font: inherit; font-size: .88rem; }
.orders-count { color: #77839a; font-size: .84rem; white-space: nowrap; }
.orders-list { display: grid; gap: 12px; }
.order-card { width: 100%; padding: 18px 20px 15px; border: 1px solid #e2e8f0; border-radius: 16px; background: var(--card-bg, #fff); color: inherit; text-align: right; cursor: pointer; box-shadow: 0 5px 16px rgba(15, 23, 42, .035); transition: border-color .16s, transform .16s, box-shadow .16s; }
.order-card:hover { transform: translateY(-2px); border-color: #93c5fd; box-shadow: 0 12px 26px rgba(15, 23, 42, .08); }
.order-card-top { display: flex; align-items: center; gap: 12px; color: #758198; font-size: .82rem; }
.order-date { display: inline-flex; align-items: center; gap: 6px; }
.order-number { margin-right: auto; padding: 4px 9px; border-radius: 7px; background: #f1f5f9; color: #526079; font-size: .75rem; }
.open-order { color: #2563eb; font-size: 1.65rem; line-height: .7; }
.customer-name { margin: 14px 0 12px; font-size: 1.05rem; font-weight: 800; }
.customer-info-grid { display: grid; grid-template-columns: minmax(180px, .45fr) 1fr; gap: 10px 22px; }
.info-item { display: flex; align-items: baseline; gap: 8px; min-width: 0; font-size: .85rem; }
.info-label { flex: none; color: #8993a6; }
.info-item b { overflow-wrap: anywhere; font-weight: 600; }
.order-card-bottom { display: flex; justify-content: space-between; align-items: center; gap: 12px; margin-top: 15px; padding-top: 12px; border-top: 1px solid #edf0f5; color: #7e899d; font-size: .8rem; }
.order-card-bottom strong { color: #15803d; font-size: .95rem; }
.empty-state { display: grid; place-items: center; gap: 6px; padding: 54px 16px; border: 1px dashed #cbd5e1; border-radius: 16px; color: #718096; }
.empty-state>span { font-size: 2rem; }
.empty-state strong { color: #334155; }
.empty-state p { margin: 0; font-size: .85rem; }
.detail-backdrop { position: fixed; inset: 0; z-index: 1500; display: grid; place-items: center; padding: 22px; background: rgba(15, 23, 42, .56); }
.order-detail { width: min(1120px, 100%); max-height: min(90vh, 900px); overflow: auto; padding: 24px; border: 1px solid #e3e8ef; border-radius: 20px; background: var(--card-bg, #fff); box-shadow: 0 25px 80px rgba(0, 0, 0, .25); }
.detail-header { display: flex; align-items: center; justify-content: space-between; gap: 16px; margin-bottom: 18px; }
.detail-header h2 { font-size: 1.35rem; }
.close-button { width: 40px; height: 40px; border: 1px solid #e2e8f0; border-radius: 50%; background: transparent; color: #64748b; font-size: 1.7rem; cursor: pointer; }
.detail-customer { display: flex; justify-content: space-between; align-items: center; gap: 18px; margin-bottom: 18px; padding: 14px 16px; border: 1px solid #e5eaf1; border-radius: 14px; background: #f8fafc; }
.detail-customer-title { display: flex; align-items: center; gap: 11px; }
.customer-avatar { display: grid; place-items: center; width: 42px; height: 42px; border-radius: 13px; background: #dbeafe; color: #1d4ed8; font-weight: 800; }
.detail-customer-title div { display: grid; gap: 5px; }
.detail-customer-title div>span { color: #748096; font-size: .8rem; }
.detail-customer-title i { padding: 0 4px; font-style: normal; }
.detail-customer>p { display: grid; gap: 5px; margin: 0; font-size: .85rem; text-align: left; }
.detail-customer>p span { color: #8993a6; font-size: .76rem; }
.detail-table-wrap { overflow-x: auto; border: 1px solid #e3e8ef; border-radius: 13px; }
.detail-table { width: 100%; border-collapse: collapse; font-size: .83rem; }
.detail-table th,.detail-table td { padding: 12px 10px; border-bottom: 1px solid #edf0f5; text-align: right; white-space: nowrap; }
.detail-table thead { background: #f4f7fb; color: #536078; }
.detail-table tbody tr:last-child td { border-bottom: 1px solid #dce3ed; }
.detail-table .item-name { min-width: 185px; white-space: normal; font-weight: 700; }
.discount-value { color: #c2410c; }
.final-value { color: #15803d; font-weight: 800; }
.detail-table tfoot { background: #f8fafc; font-weight: 800; }
.detail-table tfoot th { border-bottom: 0; }
.mobile-totals { display: none; }

@media (max-width: 700px) {
  .orders-content { width: min(100% - 22px, 560px); padding-top: 17px; }
  .page-heading { align-items: flex-start; }
  .page-heading p { max-width: 260px; line-height: 1.6; }
  .demo-badge { padding: 6px 9px; font-size: .7rem; }
  .orders-toolbar { align-items: stretch; flex-direction: column; gap: 9px; }
  .search-box { width: auto; }
  .orders-count { align-self: flex-start; }
  .order-card { padding: 15px 14px 13px; border-radius: 14px; }
  .customer-info-grid { grid-template-columns: 1fr; gap: 8px; }
  .address-item { align-items: flex-start; }
  .detail-backdrop { display: block; overflow-y: auto; padding: 0; }
  .order-detail { width: 100%; min-height: 100%; max-height: none; padding: 18px 13px 24px; border: 0; border-radius: 0; }
  .detail-header { margin-bottom: 14px; }
  .detail-customer { align-items: stretch; flex-direction: column; gap: 12px; padding: 12px; }
  .detail-customer>p { text-align: right; line-height: 1.7; }
  .detail-table-wrap { overflow: visible; border: 0; }
  .detail-table,.detail-table tbody { display: block; width: 100%; }
  .detail-table thead { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; }
  .detail-table tbody { display: grid; gap: 10px; }
  .detail-table tbody tr { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0 12px; padding: 8px 12px; border: 1px solid #e3e8ef; border-radius: 13px; background: var(--card-bg, #fff); box-shadow: 0 3px 12px rgba(15,23,42,.035); }
  .detail-table tbody td { display: flex; justify-content: space-between; align-items: baseline; gap: 9px; min-width: 0; padding: 9px 0; border-bottom: 1px solid #f0f2f6; white-space: normal; text-align: left; font-size: .79rem; overflow-wrap: anywhere; }
  .detail-table tbody td::before { content: attr(data-label); flex: none; color: #8490a4; font-size: .72rem; font-weight: 500; }
  .detail-table tbody td.item-name { grid-column: 1 / -1; grid-row: 1; min-width: 0; text-align: right; font-size: .9rem; }
  .detail-table tbody td.item-name::before { color: #536078; font-weight: 700; }
  .detail-table tbody td:nth-child(5),.detail-table tbody td:nth-child(6) { border-bottom: 0; }
  .detail-table tfoot { display: none; }
  .mobile-totals { display: grid; gap: 0; margin-top: 13px; padding: 5px 13px; border: 1px solid #dce3ed; border-radius: 13px; background: #f8fafc; }
  .mobile-totals>div { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 11px 0; font-size: .84rem; }
  .mobile-totals>div+div { border-top: 1px solid #e7ebf1; }
  .mobile-totals span { color: #6f7c91; }
  .mobile-totals strong { text-align: left; font-size: .86rem; }
}
</style>
